/**
 * @file Success Modal Component
 * @description Toast-style notification modal for success and error messages
 */

import React, { useState, useRef, useCallback } from 'react';
import '../styles/ApprovalSummary.css';

/**
 * useSuccessModal Hook
 *
 * @hook
 * @description Custom hook to manage success/error modal state and behavior.
 * Provides methods to open and close the modal with auto-close functionality.
 *
 * @returns {Object} Modal state and control methods
 * @returns {boolean} return.isOpen - Whether modal is currently visible
 * @returns {string} return.message - Current message to display
 * @returns {string} return.type - Modal type ('success' or 'reject')
 * @returns {Function} return.open - Function to open modal with message
 * @returns {Function} return.close - Function to close modal
 *
 * @example
 * const successModal = useSuccessModal();
 *
 * // Show success message
 * successModal.open('Form approved successfully!');
 *
 * // Show error message with custom duration
 * successModal.open('Approval failed', 5000, 'reject');
 *
 * // Close manually
 * successModal.close();
 */
export const useSuccessModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [type, setType] = useState('success'); // 'success' or 'reject'
  const timerRef = useRef(null);

  const open = useCallback((msg, autoCloseMs = 3000, modalType = 'success') => {
    setMessage(msg);
    setType(modalType);
    setIsOpen(true);

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Set auto-close timer if duration specified
    if (autoCloseMs > 0) {
      timerRef.current = setTimeout(() => {
        setIsOpen(false);
      }, autoCloseMs);
    }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return {
    isOpen,
    message,
    type,
    open,
    close
  };
};

/**
 * SuccessModal Component
 *
 * @component
 * @description A toast-style notification that slides in from the right.
 * Displays success (green) or error (red) messages with auto-close functionality.
 *
 * Features:
 * - Slide-in animation from right
 * - Auto-close after specified duration
 * - Click to dismiss
 * - Color-coded by type (success = green, reject = red)
 * - Icon indicator (✓ for success, ✕ for error)
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {string} props.message - Message to display
 * @param {string} [props.type='success'] - Modal type ('success' or 'reject')
 * @param {Function} props.onClose - Callback when modal is closed
 *
 * @returns {JSX.Element|null} Toast notification or null if not open
 *
 * @example
 * <SuccessModal
 *   isOpen={showModal}
 *   message="Form approved successfully!"
 *   type="success"
 *   onClose={() => setShowModal(false)}
 * />
 *
 * @example
 * <SuccessModal
 *   isOpen={showModal}
 *   message="Approval failed"
 *   type="reject"
 *   onClose={() => setShowModal(false)}
 * />
 */
export const SuccessModal = ({ isOpen, message, type = 'success', onClose }) => {
  if (!isOpen) return null;

  const isReject = type === 'reject';

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      <div className="approval-summary-toast-container" onClick={onClose}>
        <div
          className="approval-summary-toast-card"
          style={{
            borderLeft: isReject ? '4px solid #ef4444' : '4px solid #10b981'
          }}
        >
          <span
            className="approval-summary-toast-icon"
            style={{
              color: isReject ? '#ef4444' : '#10b981'
            }}
          >
            {isReject ? '✕' : '✓'}
          </span>
          <div className="approval-summary-toast-content">{message}</div>
          <span className="approval-summary-toast-close" onClick={onClose}>×</span>
        </div>
      </div>
    </>
  );
};

export default SuccessModal;
